// Chrome leaves frames that never loaded an address of their own (no src, about:blank,
// javascript:, and popups opened on about:blank) outside the service worker that controls the
// page creating them; srcdoc frames it does put under it. Requests from such a frame to proxied
// addresses then miss the proxy and reach its own web server. Pages make these frames all the
// time to run tag loaders, ads and widgets in (Akamai mPulse, Google Publisher Tag, editors).
//
// The requests a script makes there (fetch, XMLHttpRequest, sendBeacon) are made instead from
// the closest same-origin window that the service worker does control: its "carrier".

import { SCRAMJETCLIENT } from "@/symbols";
import { ScramjetClient } from "@client/index";
import { iswindow } from "@client/entry";
import { QP } from "@/fetch/parse";

const carriers = new WeakMap<ScramjetClient, Window | null>();

function controlled(client: ScramjetClient): boolean {
	try {
		return !!client.descriptors.get(
			"ServiceWorkerContainer.prototype.controller",
			client.serviceWorker
		);
	} catch {
		return false;
	}
}

function clientOf(win: any): ScramjetClient | null {
	try {
		return (win && win[SCRAMJETCLIENT]) || null;
	} catch {
		// cross-origin
		return null;
	}
}

/** The window whose requests this client's requests should be, or null to make them here. */
export function carrier(client: ScramjetClient): Window | null {
	if (!iswindow || !client.serviceWorker) return null;
	const known = carriers.get(client);
	if (known !== undefined) {
		if (known === null || (!known.closed && clientOf(known))) return known;
	}

	let found: Window | null = null;
	if (!controlled(client)) {
		const seen = new Set<any>();
		let win: any = client.global;
		while (win && !seen.has(win)) {
			seen.add(win);
			let next: any = null;
			try {
				next = win.parent !== win ? win.parent : win.opener;
			} catch {
				break;
			}
			const other = clientOf(next);
			if (!other) break;
			if (controlled(other)) {
				found = next;
				break;
			}
			win = next;
		}
	}
	// A controlled frame stays controlled; an uncontrolled one is looked at again only if its
	// carrier goes away (or none was found, as the opener can still appear later).
	if (found || controlled(client)) carriers.set(client, found);

	return found;
}

/** The carrier's own (unhooked) function or constructor `name`, with its client. */
export function carrierNative(via: Window, name: string): any {
	const other = clientOf(via);

	return other ? other.natives.store[name] : null;
}

// Elements in such a frame load their files themselves, which no hook can redirect. Scripts,
// pictures and stylesheets are fetched through the carrier instead and given to the element as
// a blob: address once they are here. A script with neither address nor text does not run when
// it is inserted, but as soon as it gets an address, so it still runs once, just a little later.
// Rewritten stylesheets only hold absolute addresses, so the blob's own address does not matter
// to them (their fonts and pictures still load from the frame). Module scripts would resolve
// their imports against the blob, and media is too large to hold: those load from the frame.
const DEFERRED: Record<string, { attr: string; dest: string }> = {
	script: { attr: "src", dest: "script" },
	img: { attr: "src", dest: "image" },
	link: { attr: "href", dest: "style" },
};

/** The proxied addresses of scripts written as blob: addresses (client/dom/document.ts). */
export const blobSources = new Map<string, string>();

/** The original addresses of elements whose files are being fetched through a carrier. */
export const deferredSources = new WeakMap<Element, string>();
const pending = new WeakMap<Element, number>();
let counter = 0;

/** Forgets a load in progress: the page has given the element another address. */
export function cancelDeferred(element: Element) {
	deferredSources.delete(element);
	pending.delete(element);
}

/**
 * Loads `proxied` into `element` through a carrier if the element's frame needs one. Returns
 * false if it does not, and the attribute should be set as usual.
 */
export function deferLoad(
	client: ScramjetClient,
	element: Element,
	tagName: string,
	attr: string,
	proxied: string,
	attrs: Record<string, string | undefined>
): boolean {
	const kind = DEFERRED[tagName];
	if (!kind || kind.attr !== attr) return false;
	if (tagName === "script" && attrs.type && attrs.type.toLowerCase() === "module")
		return false;
	if (
		tagName === "link" &&
		!(attrs.rel || "").toLowerCase().split(/\s+/).includes("stylesheet")
	)
		return false;

	let owner: ScramjetClient | undefined;
	try {
		owner = client.box.documents.get(element.ownerDocument);
	} catch {}
	if (!owner) return false;
	const via = carrier(owner);
	const viaFetch = via && carrierNative(via, "fetch");
	if (!viaFetch) return false;

	const win: any = owner.global;
	let url: URL;
	try {
		url = new win.URL(proxied, win.location.href);
	} catch {
		return false;
	}
	// The carrier's origin, not this frame's location: an untouched about:blank frame has the
	// page's origin, but its location's is "null" (Cloudflare's script frame on every page
	// with its bot detection).
	if (url.origin !== via!.location.origin) return false;
	url.searchParams.set(QP.destination, kind.dest);
	url.searchParams.set(QP.mode, "no-cors");

	deferredSources.set(element, owner.unrewriteUrl(url.href.replace(/\?.*$/, "")));
	const token = ++counter;
	pending.set(element, token);
	const setReal = (value: string) => {
		if (pending.get(element) !== token) return; // the page has set another address since
		pending.delete(element);
		owner.natives.call("Element.prototype.setAttribute", element, attr, value);
	};

	viaFetch
		.call(via, url.href, { credentials: "include" })
		.then((response: Response) => {
			if (!response.ok) throw new Error(String(response.status));

			return response.blob();
		})
		.then((blob: Blob) => {
			const blobUrl = owner.natives.call("URL.createObjectURL", win.URL, blob);
			// The file is in use once loaded (or refused); the address is not needed after that.
			const done = () =>
				win.setTimeout(
					() => owner.natives.call("URL.revokeObjectURL", win.URL, blobUrl),
					0
				);
			for (const type of ["load", "error"])
				owner.natives.call(
					"EventTarget.prototype.addEventListener",
					element,
					type,
					done,
					{ once: true }
				);
			setReal(blobUrl);
		})
		.catch(() => {
			// Loaded from the frame after all: it fails there as it would have.
			setReal(proxied);
		});

	return true;
}

/** Whether this client's frame makes its requests through a carrier. */
export function needsCarrier(client: ScramjetClient): boolean {
	const via = carrier(client);

	return !!(via && carrierNative(via, "fetch"));
}

/**
 * Fetches `proxied` through the client's carrier and resolves to a blob: address for it in the
 * client's frame, or to null if that failed. Returns null if the frame needs no carrier.
 */
export function carrierBlob(
	client: ScramjetClient,
	proxied: string,
	dest: string
): Promise<string | null> | null {
	const via = carrier(client);
	const viaFetch = via && carrierNative(via, "fetch");
	if (!viaFetch) return null;

	const win: any = client.global;
	let url: URL;
	try {
		url = new win.URL(proxied, win.location.href);
	} catch {
		return null;
	}
	if (url.origin !== via!.location.origin) return null;
	url.searchParams.set(QP.destination, dest);
	url.searchParams.set(QP.mode, "no-cors");

	return viaFetch
		.call(via, url.href, { credentials: "include" })
		.then((response: Response) => {
			if (!response.ok) throw new Error(String(response.status));

			return response.blob();
		})
		.then((blob: Blob) =>
			client.natives.call("URL.createObjectURL", win.URL, blob)
		)
		.catch(() => null);
}
